import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PLATFORMS } from "../lib/constants";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";

export default function ApplyModder() {
  const { user, loading: authLoading, isMod } = useAuth();
  const navigate = useNavigate();
  const [existing, setExisting] = useState(undefined);
  const [form, setForm] = useState({ gamertag: "", platform: "", experience: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/modder-applications/mine").then(({ data }) => setExisting(data && data.id ? data : null)).catch(() => setExisting(null));
  }, [user]);

  if (!authLoading && !user) return <Navigate to="/" replace />;
  if (authLoading || existing === undefined) return <div className="flex justify-center py-32 text-[#64748B]"><Loader2 className="animate-spin" /></div>;

  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const inputCls = "mt-1.5 border-[#1E293F] bg-[#141B2D] text-white placeholder:text-[#64748B]";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gamertag || !form.platform || !form.reason) { toast.error("Fill out the required fields."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/modder-applications", form);
      setExisting(data);
      toast.success("Application submitted! The team will review it soon.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit.");
    } finally { setSubmitting(false); }
  };

  const StatusView = () => {
    const s = existing.status;
    const meta = s === "approved"
      ? { icon: CheckCircle2, color: "#00FF87", title: "You're approved!", text: "Welcome to the modder team. Head to the Mod Queue to start vetting reports." }
      : s === "rejected"
      ? { icon: XCircle, color: "#FF2A55", title: "Not approved this time", text: "Thanks for applying. Keep reporting cheaters and you can apply again later." }
      : { icon: Clock, color: "#FFB800", title: "Application under review", text: "Your application is in the queue. We'll notify you when the team makes a decision." };
    return (
      <div className="animate-fade-up rounded-2xl border border-[#1E293F] bg-[#0F1424] p-8 text-center" data-testid="application-status">
        <meta.icon className="mx-auto" size={40} style={{ color: meta.color }} />
        <h2 className="mt-3 font-heading text-2xl font-bold text-white">{meta.title}</h2>
        <p className="mx-auto mt-2 max-w-md text-[#94A3B8]">{meta.text}</p>
        {s === "approved" && <Button onClick={() => navigate("/admin")} className="mt-5 bg-[#00F0FF] font-bold text-[#090C15] hover:bg-[#00F0FF]/90">Go to Mod Queue</Button>}
        {s === "rejected" && <Button onClick={() => setExisting(null)} variant="outline" className="mt-5 border-[#1E293F] bg-[#141B2D] text-white hover:bg-[#1A233A] hover:text-white">Apply Again</Button>}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <div className="animate-fade-up">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#00F0FF]">
          <ShieldCheck size={13} /> Join the Team
        </div>
        <h1 className="mt-4 font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">Apply to be a Modder</h1>
        <p className="mt-2 text-[#94A3B8]">Modders vet reports and help get cheaters banned. Tell us why you'd be a great fit.</p>
      </div>

      <div className="mt-8">
        {isMod ? (
          <div className="rounded-2xl border border-[#00F0FF]/30 bg-[#0F1424] p-8 text-center" data-testid="already-mod">
            <ShieldCheck className="mx-auto text-[#00F0FF]" size={40} />
            <h2 className="mt-3 font-heading text-2xl font-bold text-white">You're already a modder 🛡️</h2>
            <Button onClick={() => navigate("/admin")} className="mt-5 bg-[#00F0FF] font-bold text-[#090C15] hover:bg-[#00F0FF]/90">Go to Mod Queue</Button>
          </div>
        ) : existing ? (
          <StatusView />
        ) : (
          <form onSubmit={submit} className="animate-fade-up space-y-5 rounded-2xl border border-[#1E293F] bg-[#0F1424] p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-[#CBD5E1]">Your main gamertag *</Label>
                <Input data-testid="apply-gamertag" value={form.gamertag} onChange={(e) => set("gamertag")(e.target.value)} className={inputCls} placeholder="e.g. HoopsWatcher" />
              </div>
              <div>
                <Label className="text-[#CBD5E1]">Main platform *</Label>
                <Select value={form.platform} onValueChange={set("platform")}>
                  <SelectTrigger data-testid="apply-platform" className="mt-1.5 border-[#1E293F] bg-[#141B2D] text-white"><SelectValue placeholder="Select platform" /></SelectTrigger>
                  <SelectContent className="border-[#1E293F] bg-[#141B2D] text-white">
                    {PLATFORMS.map((p) => <SelectItem key={p} value={p} className="focus:bg-[#1A233A] focus:text-white">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[#CBD5E1]">2K experience <span className="text-[#64748B]">(hours, rep, how long you've played)</span></Label>
              <Textarea data-testid="apply-experience" value={form.experience} onChange={(e) => set("experience")(e.target.value)} rows={3} className={inputCls} placeholder="e.g. Been playing since 2K16, hit max rep twice, ~4000 hours..." />
            </div>
            <div>
              <Label className="text-[#CBD5E1]">Why do you want to be a modder? *</Label>
              <Textarea data-testid="apply-reason" value={form.reason} onChange={(e) => set("reason")(e.target.value)} rows={4} className={inputCls} placeholder="Tell us how you'd spot cheaters and why you'd be a fair, reliable vetter." />
            </div>
            <Button data-testid="apply-submit" type="submit" disabled={submitting} className="w-full bg-[#00F0FF] font-bold text-[#090C15] hover:bg-[#00F0FF]/90">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit Application
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
